/**
 * PublishSheet.tsx — 2004va
 *
 * Shown when a creator or venue taps "Publish to your channels".
 * Lists their connected channels, checks spec, generates caption,
 * queues for publish or triggers download pack.
 *
 * API connections (Instagram, YouTube etc) are phase 2 pending Meta approval.
 * Download pack and webhook are live now.
 * Spotify Canvas: packages to spec, manual upload with guidance.
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Download, Webhook, Music2, Camera, Video,
  Check, ChevronRight, X, Plus, AlertCircle, CheckCircle, Clock,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import {
  CHANNEL_SPECS, suggestCaption,
  type ChannelType,
} from '@/lib/channelSpecs'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'

// ── Types ─────────────────────────────────────────────────────────────────────

interface PublisherChannel {
  id:           string
  channel_type: ChannelType
  channel_name: string | null
  auto_publish: boolean
  min_grade:    string
  active:       boolean
}

interface PublishQueueItem {
  id:          string
  status:      string
  published_at: string | null
  published_url: string | null
  channel_id:  string
}

interface Props {
  mediaItemId:   string
  cloudinaryUrl: string
  mediaType:     string
  fileName:      string
  grade?:        string
  description?:  string
  mood?:         string
  tags?:         string[]
  eventName?:    string
  onClose:       () => void
}

// ── Channel icon ──────────────────────────────────────────────────────────────

function ChannelIcon({ type, size = 16 }: { type: ChannelType; size?: number }) {
  switch (type) {
    case 'instagram':      return <Camera     size={size} />
    case 'youtube':        return <Video      size={size} />
    case 'spotify_canvas': return <Music2     size={size} />
    case 'download_pack':  return <Download   size={size} />
    case 'webhook':        return <Webhook    size={size} />
    default:               return <ChevronRight size={size} />
  }
}

const CHANNEL_COLOUR: Partial<Record<ChannelType, string>> = {
  instagram:      'text-pink-400',
  youtube:        'text-red-400',
  tiktok:         'text-white',
  spotify_canvas: 'text-green-400',
  twitter_x:      'text-sky-400',
  linkedin:       'text-blue-400',
  download_pack:  'text-room-accent',
  webhook:        'text-purple-400',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

async function fetchMyChannels(participantId: string): Promise<PublisherChannel[]> {
  const { data, error } = await supabase
    .from('publisher_channels')
    .select('id, channel_type, channel_name, auto_publish, min_grade, active')
    .eq('participant_id', participantId)
    .eq('active', true)
    .order('created_at')
  if (error) throw error
  return (data ?? []) as PublisherChannel[]
}

async function fetchPublishStatus(mediaItemId: string): Promise<PublishQueueItem[]> {
  const { data, error } = await supabase
    .from('publish_queue')
    .select('id, status, published_at, published_url, channel_id')
    .eq('media_item_id', mediaItemId)
  if (error) throw error
  return (data ?? []) as PublishQueueItem[]
}

async function queuePublish(params: {
  mediaItemId:  string
  channelId:    string
  participantId: string
  publishData:  Record<string, unknown>
}): Promise<void> {
  const { error } = await supabase
    .from('publish_queue')
    .upsert({
      media_item_id:  params.mediaItemId,
      channel_id:     params.channelId,
      participant_id: params.participantId,
      status:         'pending',
      publish_data:   params.publishData,
    }, { onConflict: 'media_item_id,channel_id' })
  if (error) throw error
}

// ── Main ──────────────────────────────────────────────────────────────────────

export function PublishSheet({
  mediaItemId, cloudinaryUrl, mediaType, fileName,
  grade, description = '', mood, tags = [], eventName, onClose,
}: Props) {
  const { participant }   = useAuthStore()
  const qc                = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [queuing,  setQueuing]  = useState(false)
  const [done,     setDone]     = useState(false)

  const { data: channels = [], isLoading } = useQuery<PublisherChannel[]>({
    queryKey: ['my-channels', participant?.id],
    queryFn:  () => fetchMyChannels(participant!.id),
    enabled:  !!participant,
    staleTime: 5 * 60_000,
  })

  const { data: publishStatus = [] } = useQuery<PublishQueueItem[]>({
    queryKey: ['publish-status', mediaItemId],
    queryFn:  () => fetchPublishStatus(mediaItemId),
    staleTime: 10_000,
  })

  const statusByChannel: Record<string, PublishQueueItem> = {}
  publishStatus.forEach(p => { statusByChannel[p.channel_id] = p })

  // Always show download_pack as an option even if not configured
  const hasDownloadPack = channels.some(c => c.channel_type === 'download_pack')
  const displayChannels = hasDownloadPack
    ? channels
    : [...channels, {
        id: 'download_pack_default',
        channel_type: 'download_pack' as ChannelType,
        channel_name: 'Download pack',
        auto_publish: false,
        min_grade: 'D',
        active: true,
      }]

  function toggleChannel(id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  async function handlePublish() {
    if (!participant || selected.size === 0) return
    setQueuing(true)

    try {
      const caption = suggestCaption({
        channel:     'instagram', // use instagram as default
        description,
        mood,
        tags,
        event_name: eventName,
        creator_handle: participant.handle,
      })

      await Promise.all(
        Array.from(selected).map(channelId => {
          const ch = displayChannels.find(c => c.id === channelId)
          if (!ch) return Promise.resolve()

          if (ch.channel_type === 'download_pack') {
            // Trigger download directly
            const link  = document.createElement('a')
            link.href   = cloudinaryUrl
            link.download = fileName
            link.click()
            return Promise.resolve()
          }

          return queuePublish({
            mediaItemId,
            channelId,
            participantId: participant.id,
            publishData: {
              caption,
              title:      description.slice(0, 100),
              tags,
              event_name: eventName,
              grade,
            },
          })
        })
      )

      qc.invalidateQueries({ queryKey: ['publish-status', mediaItemId] })
      setDone(true)
    } catch (e) {
      console.error('Publish error:', e)
    } finally {
      setQueuing(false)
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 z-50 max-w-lg mx-auto px-4 pb-4">
        <div className="bg-room-bg border border-room-border rounded-2xl overflow-hidden shadow-2xl">

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-room-border">
            <div>
              <p className="text-white text-sm font-semibold">Publish</p>
              <p className="text-room-muted text-xs mt-0.5">Select channels to publish to</p>
            </div>
            <button type="button" onClick={onClose}
              className="p-1 rounded-lg text-room-muted hover:text-white transition-colors">
              <X size={15} />
            </button>
          </div>

          <div className="p-3 space-y-3">
            {isLoading ? (
              <div className="flex justify-center py-6"><Spinner size={20} /></div>
            ) : done ? (
              <div className="flex flex-col items-center gap-2 py-6">
                <CheckCircle size={28} className="text-emerald-400" />
                <p className="text-white font-semibold text-sm">Queued for publish</p>
                <p className="text-room-muted text-xs">
                  API channels publish when connected. Download pack delivered immediately.
                </p>
              </div>
            ) : (
              <>
                {/* Channel list */}
                <div className="space-y-1.5">
                  {displayChannels.map(ch => {
                    const spec    = CHANNEL_SPECS[ch.channel_type]
                    const status  = statusByChannel[ch.id]
                    const eligible = spec.accepts.includes(mediaType as never)
                    const isSelected = selected.has(ch.id)
                    const isPublished = status?.status === 'published'
                    const isPending   = status?.status === 'pending' || status?.status === 'publishing'
                    const isLive      = spec.api_available

                    return (
                      <button
                        key={ch.id}
                        type="button"
                        onClick={() => eligible && !isPublished && toggleChannel(ch.id)}
                        disabled={!eligible || isPublished}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-left',
                          isSelected
                            ? 'bg-room-accent/15 border border-room-accent/30'
                            : 'bg-room-surface border border-room-border',
                          (!eligible || isPublished) && 'opacity-40 cursor-not-allowed',
                          eligible && !isPublished && 'hover:border-room-accent/30'
                        )}
                      >
                        {/* Icon */}
                        <div className={cn(
                          'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                          'bg-room-border',
                          CHANNEL_COLOUR[ch.channel_type] ?? 'text-room-muted'
                        )}>
                          <ChannelIcon type={ch.channel_type} size={15} />
                        </div>

                        {/* Label */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-white text-xs font-semibold">
                              {ch.channel_name ?? spec.label}
                            </p>
                            {!isLive && (
                              <span className="text-[9px] text-room-muted bg-room-border px-1.5 py-0.5 rounded">
                                Phase 2
                              </span>
                            )}
                          </div>
                          <p className="text-room-muted text-[10px] truncate">{spec.description}</p>
                          {!eligible && (
                            <p className="text-amber-400 text-[10px]">
                              {spec.label} doesn't accept {mediaType}
                            </p>
                          )}
                          {spec.type === 'spotify_canvas' && eligible && (
                            <p className="text-green-400/70 text-[10px]">
                              We'll package to spec — you upload in Spotify for Artists
                            </p>
                          )}
                        </div>

                        {/* Status / check */}
                        <div className="flex-shrink-0">
                          {isPublished ? (
                            <CheckCircle size={15} className="text-emerald-400" />
                          ) : isPending ? (
                            <Clock size={15} className="text-amber-400" />
                          ) : isSelected ? (
                            <Check size={15} className="text-room-accent" />
                          ) : null}
                        </div>
                      </button>
                    )
                  })}
                </div>

                {/* Add channel prompt */}
                {channels.length === 0 && (
                  <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2.5">
                    <AlertCircle size={13} className="text-amber-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-amber-300 font-semibold">No channels connected yet</p>
                      <p className="text-[11px] text-amber-300/70 mt-0.5">
                        Add your channels in Portfolio → Settings. Download pack is always available.
                      </p>
                    </div>
                  </div>
                )}

                {/* Phase 2 note */}
                <p className="text-[10px] text-room-muted text-center px-2">
                  Phase 2 channels queue now and publish when API connections are live.
                  Download pack works immediately.
                </p>

                {/* Action */}
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={selected.size === 0 || queuing}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 py-3 rounded-xl',
                    'bg-room-accent text-white font-semibold text-sm',
                    'hover:bg-room-accent/90 transition-all shadow-lg shadow-room-accent/20',
                    'disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none'
                  )}
                >
                  {queuing ? <Spinner size={15} /> : <ChevronRight size={15} />}
                  {queuing
                    ? 'Publishing…'
                    : selected.size === 0
                    ? 'Select channels above'
                    : `Publish to ${selected.size} channel${selected.size > 1 ? 's' : ''}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// ── Channel setup component — used in Portfolio ───────────────────────────────

export function ChannelSetup({ participantId }: { participantId: string }) {
  const qc = useQueryClient()
  const [adding,  setAdding]  = useState(false)
  const [newType, setNewType] = useState<ChannelType>('instagram')
  const [newName, setNewName] = useState('')
  const [saving,  setSaving]  = useState(false)

  const { data: channels = [] } = useQuery<PublisherChannel[]>({
    queryKey: ['my-channels', participantId],
    queryFn:  () => fetchMyChannels(participantId),
    staleTime: 5 * 60_000,
  })

  async function handleAdd() {
    if (!newName.trim()) return
    setSaving(true)
    try {
      await supabase.from('publisher_channels').insert({
        participant_id: participantId,
        channel_type:   newType,
        channel_name:   newName.trim(),
        active:         true,
      })
      qc.invalidateQueries({ queryKey: ['my-channels', participantId] })
      setAdding(false)
      setNewName('')
    } catch (e) {
      console.error('Add channel error:', e)
    } finally {
      setSaving(false)
    }
  }

  async function handleRemove(id: string) {
    await supabase.from('publisher_channels').update({ active: false }).eq('id', id)
    qc.invalidateQueries({ queryKey: ['my-channels', participantId] })
  }

  const availableTypes = Object.keys(CHANNEL_SPECS) as ChannelType[]

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-room-muted">
          Your channels
        </h3>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)}
            className="flex items-center gap-1 text-xs text-room-accent font-semibold">
            <Plus size={12} /> Add
          </button>
        )}
      </div>

      {/* Add form */}
      {adding && (
        <div className="bg-room-surface border border-room-border rounded-xl p-3 space-y-3">
          <select
            value={newType}
            onChange={e => setNewType(e.target.value as ChannelType)}
            className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-room-accent/50"
          >
            {availableTypes.map(t => (
              <option key={t} value={t}>{CHANNEL_SPECS[t].label}</option>
            ))}
          </select>
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder={newType === 'instagram' ? '@your_handle' : newType === 'youtube' ? 'Channel name' : 'Display name'}
            className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2 text-xs text-white placeholder:text-room-muted focus:outline-none focus:border-room-accent/50"
          />
          {CHANNEL_SPECS[newType].notes && (
            <p className="text-[10px] text-room-muted">{CHANNEL_SPECS[newType].notes}</p>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => { setAdding(false); setNewName('') }}
              className="flex-1 py-2 rounded-xl border border-room-border text-xs text-room-muted hover:text-white transition-colors">
              Cancel
            </button>
            <button type="button" onClick={handleAdd} disabled={!newName.trim() || saving}
              className="flex-1 py-2 rounded-xl bg-room-accent text-white text-xs font-semibold disabled:opacity-40">
              {saving ? 'Saving…' : 'Add channel'}
            </button>
          </div>
        </div>
      )}

      {/* Channel list */}
      {channels.length === 0 && !adding && (
        <p className="text-room-muted text-xs text-center py-4">
          No channels yet — add your Instagram, YouTube, Spotify and more
        </p>
      )}

      <div className="space-y-1.5">
        {channels.map(ch => {
          const spec = CHANNEL_SPECS[ch.channel_type]
          return (
            <div key={ch.id}
              className="flex items-center gap-3 bg-room-surface border border-room-border rounded-xl px-3 py-2.5">
              <div className={cn('w-7 h-7 rounded-lg bg-room-border flex items-center justify-center',
                CHANNEL_COLOUR[ch.channel_type] ?? 'text-room-muted')}>
                <ChannelIcon type={ch.channel_type} size={13} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-semibold">{ch.channel_name ?? spec.label}</p>
                <p className="text-room-muted text-[10px]">{spec.label}</p>
              </div>
              <button type="button" onClick={() => handleRemove(ch.id)}
                className="text-room-muted hover:text-room-error transition-colors p-1">
                <X size={12} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
